import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useHouseholdRole } from "@/hooks/useHouseholdRole";
import { useAdminRole } from "@/hooks/useAdminRole";

type GuardKind =
  | "block_children"   // parents + non-household users allowed; children blocked
  | "admin_only";      // only admins allowed

interface RoleGuardProps {
  kind: GuardKind;
  children: ReactNode;
  /** Where to send blocked children. Defaults to /dashboard. */
  redirectTo?: string;
}

/**
 * Hard route protection for sensitive surfaces.
 *
 * - `block_children`: keeps child accounts out of /billing, /parent-dashboard,
 *   household management, etc. Server-side enforcement also exists via the
 *   `current_household_role` RPC and tightened subscription_records RLS, but
 *   this guard prevents the route from rendering and surfacing data fetches
 *   in the first place.
 * - `admin_only`: keeps non-admins out of admin tooling.
 */
export default function RoleGuard({ kind, children, redirectTo = "/dashboard" }: RoleGuardProps) {
  const householdRole = useHouseholdRole();
  const adminRole = useAdminRole();

  const loading = kind === "admin_only" ? adminRole.loading : householdRole.loading;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (kind === "admin_only") {
    if (!adminRole.isAdmin) {
      // Phase 5A-Fix: non-admins (incl. beta learners) get a clean redirect
      // to the dashboard instead of a restricted/denied screen.
      return <Navigate to={redirectTo} replace />;
    }
    return <>{children}</>;
  }

  // block_children
  if (householdRole.isChild) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}
